import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import '../styles/ManualBatchRegistration.css';

interface Collaboration {
  _id: string;
  handle: string;
  email: string;
  platform: string;
  agreedPrice: number;
  collaborationStage: string;
  createdAt: string;
  postUrlTikTok?: string;
  postUrlInstagram?: string;
  postUrlYoutube?: string;
  totalViews?: number;
  postMetrics?: {
    primaryViews?: number;
  };
}

const ManualBatchRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [handle, setHandle] = useState('');
  const [searching, setSearching] = useState(false);
  const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchData, setBatchData] = useState({
    batchPrice: 0,
    batchSize: 0,
    batchInterval: 7,
    status: 'completed' as 'pending' | 'accepted' | 'completed',
    batchCount: 1,
    primaryPlatform: 'tiktok'
  });
  const [submitting, setSubmitting] = useState(false);

  const searchCollaborations = async () => {
    if (!handle.trim()) {
      alert('Please enter a handle');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/collaborations/by-handle/${handle}`);
      const result = await response.json();
      
      if (result.success) {
        if (result.data.length === 0) {
          alert('No collaborations found for this handle');
        } else {
          setCollaborations(result.data);
          setStep(2);
        }
      } else {
        alert(result.error || 'Failed to search collaborations');
      }
    } catch (error) {
      console.error('Error searching collaborations:', error);
      alert('Failed to search collaborations');
    } finally {
      setSearching(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(sid => sid !== id)
        : [...prev, id]
    );
  };

  const proceedToStep3 = () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one collaboration');
      return;
    }

    // Calculate common price (average)
    const selectedCollabs = collaborations.filter(c => selectedIds.includes(c._id));
    const avgPrice = Math.round(
      selectedCollabs.reduce((sum, c) => sum + c.agreedPrice, 0) / selectedCollabs.length
    );

    setBatchData(prev => ({
      ...prev,
      batchPrice: avgPrice,
      batchSize: selectedIds.length,
      primaryPlatform: selectedCollabs[0].platform || 'tiktok'
    }));

    setStep(3);
  };

  const submitBatch = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/manual-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          handle,
          collaborationIds: selectedIds,
          ...batchData
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Batch registered successfully!');
        navigate('/batch');
      } else {
        alert(result.error || 'Failed to register batch');
      }
    } catch (error) {
      console.error('Error registering batch:', error);
      alert('Failed to register batch');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStageColor = (stage: string) => {
    const colors: { [key: string]: string } = {
      completed: '#10b981',
      awaiting_post: '#f59e0b',
      reviewing_post: '#3b82f6',
      processing_payment: '#8b5cf6',
      awaiting_script: '#f59e0b',
      reviewing_script: '#3b82f6',
      awaiting_video: '#f59e0b',
      reviewing_video: '#3b82f6'
    };
    return colors[stage] || '#6b7280';
  };

  return (
    <div className="manual-batch-container">
      <div className="header">
        <button className="back-button" onClick={() => navigate('/batch')}>
          ← Back to Batches
        </button>
        <h1>Register Manual Batch</h1>
      </div>

      <div className="registration-card">
        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Enter Handle</span>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Select Collaborations</span>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Configure Batch</span>
          </div>
        </div>

        {/* Step 1: Handle Input */}
        {step === 1 && (
          <div className="step-content">
            <h2>Step 1: Enter Creator Handle</h2>
            <p className="step-description">
              Enter the handle of the creator to search for their collaborations
            </p>
            
            <div className="handle-input-group">
              <input
                type="text"
                placeholder="@handle or handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value.replace('@', ''))}
                onKeyPress={(e) => e.key === 'Enter' && searchCollaborations()}
                className="handle-input"
              />
              <button 
                onClick={searchCollaborations}
                disabled={searching}
                className="search-button"
              >
                {searching ? 'Searching...' : 'Search Collaborations'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Select Collaborations */}
        {step === 2 && (
          <div className="step-content">
            <h2>Step 2: Select Collaborations</h2>
            <p className="step-description">
              Select the collaborations to include in this batch ({selectedIds.length} selected)
            </p>
            
            <div className="collaborations-table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedIds.length === collaborations.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(collaborations.map(c => c._id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </th>
                    <th>Platform</th>
                    <th>Price</th>
                    <th>Stage</th>
                    <th>Created</th>
                    <th>Views</th>
                    <th>Posts</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborations.map(collab => (
                    <tr key={collab._id} className={selectedIds.includes(collab._id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(collab._id)}
                          onChange={() => toggleSelection(collab._id)}
                        />
                      </td>
                      <td>
                        <span className="platform-badge">{collab.platform}</span>
                      </td>
                      <td>${collab.agreedPrice}</td>
                      <td>
                        <span style={{ color: getStageColor(collab.collaborationStage), fontWeight: '500', fontSize: '13px' }}>
                          {collab.collaborationStage.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{formatDate(collab.createdAt)}</td>
                      <td>{collab.postMetrics?.primaryViews || collab.totalViews || '-'}</td>
                      <td>
                        <div className="post-links">
                          {collab.postUrlTikTok && <a href={collab.postUrlTikTok} target="_blank" rel="noopener noreferrer">TT</a>}
                          {collab.postUrlInstagram && <a href={collab.postUrlInstagram} target="_blank" rel="noopener noreferrer">IG</a>}
                          {collab.postUrlYoutube && <a href={collab.postUrlYoutube} target="_blank" rel="noopener noreferrer">YT</a>}
                          {!collab.postUrlTikTok && !collab.postUrlInstagram && !collab.postUrlYoutube && '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="step-actions">
              <button onClick={() => setStep(1)} className="secondary-button">
                Back
              </button>
              <button 
                onClick={proceedToStep3}
                disabled={selectedIds.length === 0}
                className="primary-button"
              >
                Continue to Configuration
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Configure Batch */}
        {step === 3 && (
          <div className="step-content">
            <h2>Step 3: Configure Batch</h2>
            <p className="step-description">
              Review and configure the batch settings
            </p>
            
            <div className="batch-config">
              <div className="config-grid">
                <div className="config-item">
                  <label>Handle</label>
                  <input type="text" value={handle} disabled />
                </div>
                
                <div className="config-item">
                  <label>Platform</label>
                  <select 
                    value={batchData.primaryPlatform}
                    onChange={(e) => setBatchData(prev => ({ ...prev, primaryPlatform: e.target.value }))}
                  >
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Price per Video</label>
                  <input 
                    type="number"
                    value={batchData.batchPrice}
                    onChange={(e) => setBatchData(prev => ({ ...prev, batchPrice: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                
                <div className="config-item">
                  <label>Batch Size (Total Videos)</label>
                  <input 
                    type="number"
                    value={batchData.batchSize}
                    onChange={(e) => setBatchData(prev => ({ ...prev, batchSize: parseInt(e.target.value) || 1 }))}
                    min={selectedIds.length}
                    title={`Minimum: ${selectedIds.length} (selected collaborations)`}
                  />
                  {batchData.batchSize > selectedIds.length && (
                    <small style={{ color: '#f59e0b', fontSize: '12px', marginTop: '4px' }}>
                      {batchData.batchSize - selectedIds.length} additional scheduled video(s) will be created
                    </small>
                  )}
                </div>
                
                <div className="config-item">
                  <label>Batch Number</label>
                  <input 
                    type="number"
                    value={batchData.batchCount}
                    onChange={(e) => setBatchData(prev => ({ ...prev, batchCount: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                
                <div className="config-item">
                  <label>Interval (days)</label>
                  <input 
                    type="number"
                    value={batchData.batchInterval}
                    onChange={(e) => setBatchData(prev => ({ ...prev, batchInterval: parseInt(e.target.value) || 7 }))}
                  />
                </div>
                
                <div className="config-item">
                  <label>Batch Status</label>
                  <select 
                    value={batchData.status}
                    onChange={(e) => setBatchData(prev => ({ ...prev, status: e.target.value as any }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="accepted">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                
                <div className="config-item">
                  <label>Total Value</label>
                  <input 
                    type="text"
                    value={`$${(batchData.batchPrice * batchData.batchSize).toLocaleString()}`}
                    disabled
                  />
                </div>
              </div>

              <div className="selected-summary">
                <h3>Batch Summary</h3>
                <p>{selectedIds.length} existing collaboration(s) selected</p>
                {batchData.batchSize > selectedIds.length && (
                  <p style={{ color: '#f59e0b', marginTop: '8px' }}>
                    + {batchData.batchSize - selectedIds.length} scheduled collaboration(s) will be created
                  </p>
                )}
                <p style={{ marginTop: '8px', fontWeight: '600' }}>
                  Total: {batchData.batchSize} videos × ${batchData.batchPrice} = ${(batchData.batchPrice * batchData.batchSize).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="step-actions">
              <button onClick={() => setStep(2)} className="secondary-button">
                Back
              </button>
              <button 
                onClick={submitBatch}
                disabled={submitting}
                className="primary-button"
              >
                {submitting ? 'Registering...' : 'Register Batch'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualBatchRegistration;